// Angular Imports
import {NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common'
import {ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal,} from '@angular/core'
// Angular Material Imports
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop'
import {MatCheckboxChange, MatCheckboxModule} from '@angular/material/checkbox'
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner'
import {MatMenuModule} from '@angular/material/menu'
// Dashboard Module Imports
// Shared Imports
import {
	EIcons,
	IAddDragAndDropColumnToColumns,
	IDragAndDropColumn,
	IDragAndDropColumnActions,
	IDragAndDropColumnActionsEmitted,
	IDragAndDropColumnStructure,
} from '@shared/models'
import {IconButtonComponent, IconComponent, PillComponent} from '@shared/components'

const components = [IconComponent, IconButtonComponent, PillComponent]

const material = [MatProgressSpinnerModule, DragDropModule, MatCheckboxModule, MatMenuModule]

@Component({
	selector: 'app-drag-and-drop-columns-table',
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		...material,
		...components,
	],
	templateUrl: './drag-and-drop-columns-table.component.html',
	styleUrls: ['./drag-and-drop-columns-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragAndDropColumnsTableComponent {
	@Input() set columns(dragAndDropColumns: Array<IDragAndDropColumn>) {
		this.dragAndDropColumns.set(dragAndDropColumns)
	}
	@Input() emptyMessage = ''
	@Input() tableHeight = 'auto'
	@Input() hasSelects = false
	@Input() hasSelectsAll = false
	@Input() isLoading = false
	@Output() selectEmitted = new EventEmitter<Array<any>>()
	@Output() moveColumnChange = new EventEmitter<Array<IDragAndDropColumn>>()
	@Output() actionEmitted = new EventEmitter<IDragAndDropColumnActionsEmitted>()

	actions = signal<Array<IDragAndDropColumnActions>>([])
	hasActions = computed<boolean>(() => Boolean(this.actions().length))
	dragAndDropColumns = signal<Array<IDragAndDropColumn>>([])
	dragAndDropColumnsToShowed = computed(() =>
		this.dragAndDropColumns().filter((_column: IDragAndDropColumn) => !_column.hide)
	)
	dragAndDropColumnsToShowedLength = computed(() => this.dragAndDropColumnsToShowed().length)
	dragAndDropColumnSelected = signal<Array<any>>([])

	readonly EIcons = EIcons

	get hasActionsOrSelects(): boolean {
		return this.hasActions() || this.hasSelects
	}

	onMoveColumn(event: CdkDragDrop<Array<IDragAndDropColumn>>): void {
		const { previousIndex, currentIndex } = event

		if (previousIndex !== currentIndex) {
			moveItemInArray(this.dragAndDropColumnsToShowed(), previousIndex, currentIndex)
			this.moveColumnChange.emit(this.dragAndDropColumnsToShowed())
		}
	}

	onEmitAction(action: IDragAndDropColumnActions, columns: Array<IDragAndDropColumn>, index: number) {
		const object: any = this._rebuildObjectFromColumns(columns, index)

		const emitted: IDragAndDropColumnActionsEmitted = {
			action,
			object,
		}

		this.actionEmitted.emit(emitted)
	}

	isSelected(columns: Array<IDragAndDropColumn>, index: number): boolean {
		const object: any = this._rebuildObjectFromColumns(columns, index)
		const exists = this.dragAndDropColumnSelected().find((item) => item.id === object.id)
		return exists ? true : false
	}

	clearSelection() {
		this.dragAndDropColumnSelected.set([])
		this.selectEmitted.emit([])
	}

	onSelect(event: MatCheckboxChange, columns: Array<IDragAndDropColumn>, index: number) {
		const object: any = this._rebuildObjectFromColumns(columns, index)

		if (event.checked) {
			this.dragAndDropColumnSelected.update((previous) => [object, ...previous])
			this.selectEmitted.emit(this.dragAndDropColumnSelected())
		} else {
			this.dragAndDropColumnSelected.update((previous) =>
				previous.filter((item) => item.id !== object.id)
			)
			this.selectEmitted.emit(this.dragAndDropColumnSelected())
		}
	}

	onSelectOrUnselectAll(event: MatCheckboxChange, columns: Array<IDragAndDropColumn>) {
		const columnId = this.dragAndDropColumns().find(
			(column) => column.name === 'id'
		) as IDragAndDropColumn
		const objects: Array<any> = []

		columnId.values.map((column, index) => {
			const object: any = this._rebuildObjectFromColumns(this.dragAndDropColumns(), index)
			objects.push(object)
		})

		if (event.checked) {
			this.dragAndDropColumnSelected.update(() => objects)
			this.selectEmitted.emit(this.dragAndDropColumnSelected())
		} else {
			this.dragAndDropColumnSelected.update(() => [])
			this.selectEmitted.emit(this.dragAndDropColumnSelected())
		}
	}

	// We build each column and in case it doesn't exist or update it in case it already exists
	buildDragAndDropColumns<T>({
		columnsStructure,
		dataStructure,
		actions,
	}: IAddDragAndDropColumnToColumns<T>): Array<IDragAndDropColumn> {
		const columns: Array<IDragAndDropColumn> = []
		this.actions.set(actions)

		columnsStructure.map((column: IDragAndDropColumnStructure) => {
			dataStructure.map((dataItem: T) => {
				// We check if the property we pass it exists within the object we receive
				if (dataItem?.hasOwnProperty(column.name)) {
					const propertyValue = dataItem[column.name as keyof T]
					// We check if a column already exists in our array with the name of that property
					const findCurrentColumn: IDragAndDropColumn | undefined = columns.find(
						(_column: IDragAndDropColumn) => _column.name === column.name
					)
					if (findCurrentColumn) {
						// If it exists, we update it
						const column: IDragAndDropColumn = findCurrentColumn
						column.values = [...column.values, propertyValue]
					} else {
						// If it doesn't exist, we add it
						const columnToAdd: IDragAndDropColumn = {
							name: column.name,
							label: column.label,
							width: column.width ? column.width : 'auto',
							hide: column.hide ? true : false,
							justify: column.justify ? column.justify : 'left',
							isPill: column.isPill ? true : false,
							transform: column.transform ? column.transform : (value: any) => value,
							cssVariables: (value: any) => Object
									.entries((column.cssVariables ?? (_ => ({})))(value))
									.reduce((output, [key, val]) => output.concat(`--${key}: ${val}`), ''),
							values: [propertyValue],
						}
						columns.push(columnToAdd)
					}
				} else {
					console.error(
						`this column "${column.name}" doesn't exist in this data structure`,
						dataStructure
					)
				}
			})
		})

		return columns
	}

	rebuildDataStructure(index: number) {
		return this._rebuildObjectFromColumns(this.dragAndDropColumns(), index)
	}

	private _rebuildObjectFromColumns(columns: Array<IDragAndDropColumn>, index: number) {
		// We reconstruct the object passed to us from the selected index and the columns we receive

		/* INFO:
			Using this data as an input:

			index: 0
			columns = [
				{
					label: 'Id',
					name: 'id',
					values: ['333333', '222222'],
					width: '100px',
				},
				{
					label: 'Name',
					name: 'name',
					values: ['Buenos Aires', 'Mendoza'],
					width: 'auto',
				},
				{
					label: 'Chargers',
					name: 'chargers',
					values: [24, 27],
					width: '150px',
				},
				{
					label: 'Hoses',
					name: 'hoses',
					values: [23, 26],
					width: '150px',
				},
				{
					label: 'Drivers',
					name: 'drivers',
					values: [25, 29],
					width: '150px',
				},
			]

		 	We will receive this result:

			{
				chargers: 24
				drivers: 25
				hoses: 23
				id: '333333'
				name: 'Buenos Aires'
			}
		*/

		return columns.reduce((previous: any, current: IDragAndDropColumn) => {
			return { ...previous, [current.name]: current.values[index] }
		}, {})
	}
}
